import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import InfiniteScroll from "react-infinite-scroll-component";
import { NetworkErrorHandler, NoDataFoundComp } from "../../Utils/Constants.";
import ZoneContext from "../../Utils/Context/ZoneContext";
import { Link, withRouter, useHistory, useLocation } from "react-router-dom";
import AddOREditZone from "./AddOREditZone";
import ActionMenu from "../../components/SharedComp/ActionMenu";
import DesignModal from "../../components/SharedComp/DesignModal";

/**
//  * @component  ZoneList of Zone overview
//  * @description  ZoneList render using cards and grid css
//  * @returns ZoneList UI
//  */

// E5 design: the zone list sits in a white rounded panel
// (!important: the older nested .zoneBox .overviewBox rule sets an off-white background and border)
const listPanelSx = {
  backgroundColor: "#fff !important",
  border: "none !important",
  borderRadius: "12px !important",
  boxShadow: "0 1px 4px rgba(16,24,40,.12)",
  p: "24px 20px 12px !important",
};

function ZoneList(props) {
  const context = useContext(ZoneContext); // context value of zone
  const history = useHistory();
  const location = useLocation();

  /**
   * ⋯ → Edit: the form reads the zone from location state, then the modal opens
   */
  const openEdit = (item) => {
    history.replace({ ...location, state: { zoneDetails: item } });
    context.clearAndSetItem(item);
    context.handleOpen();
  };
  /**
   * style object for add zone dialog box
   */
  const useConfirmStyles = makeStyles({
    dialog: {
      position: "absolute",
      left: 150,
      top: 150,
    },
  });

  /**
   * style object for edit zone dialog box
   */
  const useEditStyles = makeStyles({
    dialog: {
      position: "absolute",
      backgroundColor: "none",
      background: "none",
    },
  });

  const classesTwo = useEditStyles();
  const classesThree = useConfirmStyles();

  /**
   * modal close function and clear state
   */
  const handleClose = () => {
    context.handleClose();
    setTimeout(() => {
      context.clearAndSetItem();
    }, 500);
  };
  return (
    <div>
      {context.errorThrow ? (
        NetworkErrorHandler()
      ) : context.isLoading ? (
        <CircularProgress size={40} color="info" className="circularLoader" />
      ) : context.zoneList && context.zoneList.length > 0 ? (
        <div>
          <InfiniteScroll
            dataLength={context.zoneList.length}
            next={props.fetchMoreData}
            hasMore={props.isNext}
            loader={
              <div className="infiniteLoader">
                <CircularProgress size={40} className="infiniteLoader" />
              </div>
            }
          >
            <div className="zoneTitle"></div>
            <Box sx={{ flexGrow: 1, ...listPanelSx }} className="overviewBox">
              {/* E5 design card grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: "20px 32px",
                  pb: "12px",
                }}
              >
                {context.zoneList.map((item, index) => {
                  return (
                    <Box key={item.id ? item.id : index} sx={{ position: "relative" }}>
                      <Link
                        id="ZoneCardItem"
                        title="Open Zone Configuration"
                        onClick={() => context.clearAndSetItem(item)}
                        to={{
                          pathname: "/configuration",
                          state: {
                            // location state
                            zoneData: item,
                          },
                        }}
                        style={{ textDecoration: "none", display: "block" }}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#f7f7f9",
                            borderRadius: "10px",
                            boxShadow: "0 1px 3px rgba(16,24,40,.12)",
                            p: "6px 6px 0",
                            height: 72,
                            boxSizing: "border-box",
                            "&:hover": { boxShadow: "0 2px 8px rgba(16,24,40,.18)" },
                          }}
                        >
                          <Tooltip title={item.zoneName} placement="top">
                            <Box
                              sx={{
                                backgroundColor: "#dcdbee",
                                borderRadius: "6px",
                                height: 28,
                                lineHeight: "28px",
                                textAlign: "center",
                                fontWeight: 700,
                                fontSize: 12,
                                color: "#202020",
                                px: "30px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item.zoneName}
                            </Box>
                          </Tooltip>
                          <Box
                            sx={{
                              height: 38,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#864797",
                              fontSize: 13,
                            }}
                          >
                            {item.zoneNumber}
                          </Box>
                        </Box>
                      </Link>
                      {/* E5: quiet per-zone action menu inside the title band; a sibling of the card link so it never navigates */}
                      <ActionMenu
                        ariaLabel={`Zone actions for ${item.zoneName}`}
                        sx={{ position: "absolute", top: "7px", right: "7px", zIndex: 2 }}
                        items={[
                          { label: "Edit", onClick: () => openEdit(item) },
                          {
                            label: "Deactivate",
                            destructive: true,
                            onClick: () =>
                              context.onDeactivateZone && context.onDeactivateZone(item),
                          },
                        ]}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </InfiniteScroll>
        </div>
      ) : (
        <Box sx={{ flexGrow: 1, ...listPanelSx }} className="overviewBox">
          {context.errorThrow ? null : context.searchText ? (
            <Box sx={{ p: "32px 12px 44px", color: "#666", fontSize: 12, fontWeight: 400, textAlign: "left" }}>
              No zones match "{context.searchText}".
            </Box>
          ) : (
            NoDataFoundComp()
          )}
        </Box>
      )}
      <DesignModal open={!!context.open} onClose={handleClose} labelledBy="zone-form-title">
        <AddOREditZone
          timeZoneList={context.timeZoneList}
          onModalClose={handleClose}
          classesThree={classesThree}
          classesTwo={classesTwo}
        />
      </DesignModal>
    </div>
  );
}

ZoneList.propTypes = {
  fetchMoreData: PropTypes.func,
  isNext: PropTypes.bool,
};

export default withRouter(ZoneList);
